import React, { useState, useEffect, useCallback } from 'react';
import {
  adminGetBlogs,
  adminUpdateStatus,
  adminDeleteBlog,
  adminGetLanguages,
} from '../../services/blogApi';
import { BlogPost, BlogLanguage } from '../../types/blog';

interface Props {
  secret: string;
  onNew: () => void;
  onEdit: (id: string) => void;
}

const STATUS_COLORS: Record<string, string> = {
  published: '#22c55e',
  draft: '#f59e0b',
  archived: '#9ca3af',
};

const S = {
  toolbar: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
    marginBottom: '1.25rem',
    flexWrap: 'wrap' as const,
  } as React.CSSProperties,
  select: {
    padding: '0.45rem 0.75rem',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '0.9rem',
    background: '#fff',
    fontFamily: 'inherit',
    cursor: 'pointer',
  } as React.CSSProperties,
  newBtn: {
    marginLeft: 'auto',
    padding: '0.5rem 1.25rem',
    background: 'linear-gradient(135deg, #2DD4BF 0%, #4C82FF 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    background: '#fff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
  } as React.CSSProperties,
  th: {
    padding: '0.75rem 1rem',
    textAlign: 'left' as const,
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#6b6b8a',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
  } as React.CSSProperties,
  td: {
    padding: '0.85rem 1rem',
    fontSize: '0.9rem',
    color: '#07091A',
    borderBottom: '1px solid #f3f4f6',
    verticalAlign: 'middle' as const,
  } as React.CSSProperties,
  statusBadge: (status: string) => ({
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: '999px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: `${STATUS_COLORS[status] ?? '#9ca3af'}22`,
    color: STATUS_COLORS[status] ?? '#9ca3af',
  } as React.CSSProperties),
  actionBtn: {
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
    border: '1px solid #e5e7eb',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.8rem',
    marginRight: '0.4rem',
    fontFamily: 'inherit',
    color: '#07091A',
  } as React.CSSProperties,
  dangerBtn: {
    padding: '0.3rem 0.7rem',
    borderRadius: '6px',
    border: '1px solid #fee2e2',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontFamily: 'inherit',
    color: '#ef4444',
  } as React.CSSProperties,
  loading: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: '#6b6b8a',
  } as React.CSSProperties,
};

const AdminBlogList: React.FC<Props> = ({ secret, onNew, onEdit }) => {
  const [blogs, setBlogs] = useState<BlogPost[]>([]);
  const [languages, setLanguages] = useState<BlogLanguage[]>([]);
  const [langFilter, setLangFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminGetBlogs(secret, {
        lang: langFilter || undefined,
        status: statusFilter || undefined,
        limit: 50,
      });
      setBlogs(res.data ?? []);
    } catch {
      setError('Failed to load blogs. Check your admin secret.');
    } finally {
      setLoading(false);
    }
  }, [secret, langFilter, statusFilter]);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  useEffect(() => {
    adminGetLanguages(secret)
      .then((res) => setLanguages(res.data ?? []))
      .catch(() => {});
  }, [secret]);

  const handleStatusToggle = async (blog: BlogPost) => {
    const next = blog.status === 'published' ? 'draft' : 'published';
    setActionLoading(blog.id);
    try {
      await adminUpdateStatus(secret, blog.id, next);
      await fetchBlogs();
    } catch {
      alert('Failed to update status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (blog: BlogPost) => {
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;
    setActionLoading(blog.id);
    try {
      await adminDeleteBlog(secret, blog.id);
      await fetchBlogs();
    } catch {
      alert('Failed to delete blog.');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleDateString() : '—';

  return (
    <div>
      <div style={S.toolbar}>
        <select
          style={S.select}
          value={langFilter}
          onChange={(e) => setLangFilter(e.target.value)}
        >
          <option value="">All languages</option>
          {languages.map((l) => (
            <option key={l.code} value={l.code}>
              {l.name} ({l.code})
            </option>
          ))}
        </select>

        <select
          style={S.select}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="archived">Archived</option>
        </select>

        <button style={S.newBtn} onClick={onNew}>
          + New Blog
        </button>
      </div>

      {loading && <div style={S.loading}>Loading blogs...</div>}
      {!loading && error && <div style={{ color: '#ef4444', padding: '1rem' }}>{error}</div>}

      {!loading && !error && (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Title</th>
              <th style={S.th}>Lang</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Views</th>
              <th style={S.th}>Published</th>
              <th style={S.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.length === 0 && (
              <tr>
                <td colSpan={6} style={{ ...S.td, textAlign: 'center', color: '#9ca3af' }}>
                  No blogs found.
                </td>
              </tr>
            )}
            {blogs.map((blog) => (
              <tr key={blog.id}>
                <td style={S.td}>
                  <strong>{blog.title}</strong>
                  <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '2px' }}>
                    /{blog.slug}
                  </div>
                </td>
                <td style={S.td}>
                  <span style={{ fontWeight: 600, textTransform: 'uppercase', fontSize: '0.8rem' }}>
                    {blog.language_code}
                  </span>
                </td>
                <td style={S.td}>
                  <span style={S.statusBadge(blog.status ?? 'draft')}>
                    {blog.status ?? 'draft'}
                  </span>
                </td>
                <td style={S.td}>{blog.view_count?.toLocaleString() ?? 0}</td>
                <td style={S.td}>{formatDate(blog.published_at)}</td>
                <td style={S.td}>
                  <button style={S.actionBtn} onClick={() => onEdit(blog.id)}>
                    Edit
                  </button>
                  <button
                    style={S.actionBtn}
                    disabled={actionLoading === blog.id}
                    onClick={() => handleStatusToggle(blog)}
                  >
                    {blog.status === 'published' ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    style={S.dangerBtn}
                    disabled={actionLoading === blog.id}
                    onClick={() => handleDelete(blog)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminBlogList;
